import { Card, SectionLabel } from '@wundo/ui';
import type { Message } from '@/types/messages.ts';
import { KnowledgeBaseSearchResults } from './KnowledgeBaseSearchResults.tsx';
import {
  isKnowledgeBaseSearch,
  knowledgeBaseQuery,
  knowledgeBaseResults,
} from './knowledgeBase.ts';

export interface ToolCallTurnProps {
  message: Message;
}

/**
 * A turn nobody said: a tool the agent called, or a note the system left.
 *
 * Drawn as a card across the width rather than as a bubble on one side —
 * neither of these is a party to the conversation, and both are read for their
 * contents rather than followed as speech.
 */
export function ToolCallTurn({ message }: ToolCallTurnProps) {
  const toolCall = message.meta?.tool_call;

  if (isKnowledgeBaseSearch(message)) {
    return (
      <Card tone='quiet' padding='none' className='p-3.5'>
        <KnowledgeBaseSearchResults
          results={knowledgeBaseResults(message)}
          query={knowledgeBaseQuery(message)}
        />
      </Card>
    );
  }

  if (message.type === 'system_message') {
    return (
      <Card tone='quiet' padding='none' className='p-3.5'>
        <SectionLabel as='h3'>System</SectionLabel>
        {message.body.type === 'text' && message.body.text ? (
          <p className='mt-1.5 text-sm whitespace-pre-wrap text-ink-body'>
            {message.body.text}
          </p>
        ) : null}
      </Card>
    );
  }

  return (
    <Card tone='quiet' padding='none' className='flex flex-col gap-3 p-3.5'>
      <div>
        <SectionLabel as='h3'>Tool call</SectionLabel>
        <p className='mt-0.5 text-sm font-bold text-ink'>
          {toolCall?.name || 'Tool call'}
        </p>
        {toolCall?.id ? (
          <p className='text-xs text-ink-muted'>ID: {toolCall.id}</p>
        ) : null}
      </div>

      <div className='rounded-md border border-line bg-surface px-3 py-2'>
        <p className='mb-1 text-xs font-bold text-ink-secondary'>Arguments</p>
        <pre className='overflow-x-auto text-xs leading-5 break-all whitespace-pre-wrap text-ink-body'>
          {JSON.stringify(toolCall?.args ?? {}, null, 2)}
        </pre>
      </div>

      {message.body.type === 'text' && message.body.text ? (
        <div className='rounded-md bg-fill px-3 py-2'>
          <p className='mb-1 text-xs font-bold text-ink-secondary'>Tool response</p>
          <p className='text-sm whitespace-pre-wrap text-ink-body'>{message.body.text}</p>
        </div>
      ) : null}
    </Card>
  );
}
